import React, { useEffect, useRef, useState } from 'react';
import { Captions as CaptionsIcon, ChevronDown, Info, Pause, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react';
import { PRESETS, SIMPLIFICATIONS } from '../data/content';
import { CHAPTERS, chapterLabel } from '../data/script';
import { CHAPTER_SPANS, TOTAL_MS, beatAt, formatTime } from '../lib/timeline';
import { cx } from '../lib/hooks';
type Props = {
  time: number;
  playing: boolean;
  visible: boolean;
  onTogglePlay: () => void;
  onSeek: (ms: number) => void;
  onStep: (dir: -1 | 1) => void;
  presetId: string;
  onPreset: (id: string) => void;
  captionsOn: boolean;
  onToggleCaptions: () => void;
  onResetView: () => void;
};
export function ControlBar(props: Props) {
  const {
    time,
    playing,
    visible,
    onTogglePlay,
    onStep,
    captionsOn,
    onToggleCaptions,
    onResetView
  } = props;
  const chapter = beatAt(time).chapter;
  return <div className={cx('absolute inset-x-0 bottom-0 z-30 px-5 pb-4 transition-opacity duration-500 max-sm:px-3 max-sm:pb-3', visible ? 'opacity-100' : 'pointer-events-none opacity-0')} data-id="element-135" data-mp="2h4cbxrd8yds6">
      <Scrubber time={time} onSeek={props.onSeek} data-id="element-136" data-mp="3o8q95zcu0ez8" />
      <div className="mt-1.5 flex items-center gap-1" data-id="element-137" data-mp="3t0rgr7z21hix">
        <button type="button" onClick={onTogglePlay} aria-label={playing ? 'Pause' : 'Play'} className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#07080C] transition-transform duration-150 hover:scale-105 active:scale-95" data-id="element-138" data-mp="3t19nwzn99g5m">
          {playing ? <Pause className="h-4 w-4" fill="currentColor" data-id="element-139" data-mp="35d9olbbvohff" /> : <Play className="ml-0.5 h-4 w-4" fill="currentColor" data-id="element-140" data-mp="3a8ozbuel9x7m" />}
        </button>
        <IconButton label="Previous caption" onClick={() => onStep(-1)} data-id="element-141" data-mp="3faxt2szad3as">
          <SkipBack className="h-4 w-4" data-id="element-142" data-mp="pku50ul8w1ih" />
        </IconButton>
        <IconButton label="Next caption" onClick={() => onStep(1)} data-id="element-143" data-mp="pmdpkxdxoyq2">
          <SkipForward className="h-4 w-4" data-id="element-144" data-mp="uhknfokiv972" />
        </IconButton>
        <span className="ml-1 font-mono text-[12px] tabular-nums text-[#9A9FB0]" data-id="element-145" data-mp="2qlh6k3y3z323">
          {formatTime(time)} / {formatTime(TOTAL_MS)}
        </span>
        <span className="ml-4 hidden min-w-0 truncate text-[13px] text-[#C9CCD8] md:inline" data-id="element-146" data-mp="2lp9z26q2dji1">
          <span className="mr-1.5 font-mono text-[12px] text-[#5D6272]" data-id="element-147" data-mp="2zkuid39pg3pz">
            {chapterLabel(chapter)}
          </span>
          {CHAPTERS[chapter].title}
        </span>

        <div className="ml-auto flex items-center gap-1" data-id="element-148" data-mp="27wal495xjxcl">
          <PromptMenu presetId={props.presetId} onPreset={props.onPreset} data-id="element-149" data-mp="22x7qzjrhnbje" />
          <IconButton label={captionsOn ? 'Hide captions' : 'Show captions'} onClick={onToggleCaptions} active={captionsOn} className="max-sm:hidden" data-id="element-150" data-mp="22uuzofzuboto">
            <CaptionsIcon className="h-4 w-4" data-id="element-151" data-mp="1o7rz20g81024" />
          </IconButton>
          <InfoMenu data-id="element-152" data-mp="1o5p2exkkc8of" />
          <IconButton label="Reset view" onClick={onResetView} className="max-sm:hidden" data-id="element-153" data-mp="1k66j268e45np">
            <RotateCcw className="h-4 w-4" data-id="element-154" data-mp="1k4djtc03wnpq" />
          </IconButton>
        </div>
      </div>
    </div>;
}
function Scrubber({
  time,
  onSeek
}: {
  time: number;
  onSeek: (ms: number) => void;
}) {
  return <div className="flex items-center gap-[3px]" data-id="element-155" data-mp="1043lkim88iw8">
      {CHAPTER_SPANS.map((span, c) => {
      const duration = span.end - span.start;
      const fill = Math.min(1, Math.max(0, (time - span.start) / duration));
      const title = CHAPTERS[c].title;
      return <button key={title} type="button" aria-label={`${CHAPTERS[c].short}: ${title}`} onClick={e => {
        const rect = e.currentTarget.getBoundingClientRect();
        const f = Math.min(0.999, Math.max(0, (e.clientX - rect.left) / rect.width));
        onSeek(span.start + f * duration);
      }} className="group relative h-5 min-w-0 basis-0" style={{
        flexGrow: duration
      }} data-id="element-156" data-mp="1stizph96urwd" data-mpi={c}>
            <span className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 overflow-hidden rounded-full bg-white/15 transition-[height] duration-150 group-hover:h-[6px]" data-id="element-157" data-mp="cfsrbyb1dhgx">
              <span className="block h-full bg-[#EDEEF2] transition-[width] duration-100 ease-linear" style={{
            width: `${fill * 100}%`
          }} data-id="element-158" data-mp="nu5iabba8rb7" />
            </span>
            <span className="pointer-events-none absolute bottom-full left-1/2 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-white/10 bg-[#12141D] px-2 py-1 text-[11px] text-[#C9CCD8] opacity-0 transition-opacity duration-150 group-hover:opacity-100" data-id="element-159" data-mp="dy2fr4ciqqnq">
              <span className="mr-1.5 font-mono text-[#5D6272]" data-id="element-160" data-mp="11lj09ktjlh44">{chapterLabel(c)}</span>
              {title}
            </span>
          </button>;
    })}
    </div>;
}
function PromptMenu({
  presetId,
  onPreset
}: {
  presetId: string;
  onPreset: (id: string) => void;
}) {
  const current = PRESETS.find(p => p.id === presetId) ?? PRESETS[0];
  return <Menu trigger={<>
          <span className="max-w-[180px] truncate font-mono text-[12px]" data-id="element-162" data-mp="1ww75qp38f0vz">{current.label}…</span>
          <ChevronDown className="h-3.5 w-3.5" data-id="element-163" data-mp="21tqu4htn8u1n" />
        </>} triggerClassName="flex h-8 items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.04] px-3 text-[#C9CCD8] hover:bg-white/[0.08] hover:text-[#EDEEF2]" label="Sentence to finish" data-id="element-161" data-mp="3hft21842bb1g">
      <div className="px-2 pb-1 pt-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[#5D6272]" data-id="element-164" data-mp="2gjondc6fk9xp">
        Sentence to finish
      </div>
      {PRESETS.map(p => <button key={p.id} type="button" data-close-menu onClick={() => onPreset(p.id)} className={cx('block w-full rounded-lg px-2 py-1.5 text-left font-mono text-[12px] transition-colors', p.id === presetId ? 'bg-white/[0.1] text-[#EDEEF2]' : 'text-[#9A9FB0] hover:bg-white/[0.06] hover:text-[#EDEEF2]')} data-id="element-165" data-mp="2kn311w6wce7z">
          {p.label}…
        </button>)}
    </Menu>;
}
function InfoMenu() {
  return <Menu trigger={<Info className="h-4 w-4" data-id="element-167" data-mp="o3nbbfgx6v37" />} triggerClassName="flex h-8 w-8 items-center justify-center rounded-full text-[#9A9FB0] hover:bg-white/[0.08] hover:text-[#EDEEF2]" label="What's simplified here" data-id="element-166" data-mp="o359m3bhnonw">
      <div className="w-64 p-2" data-id="element-168" data-mp="x2sdqwvlr8kn">
        <div className="text-[12px] font-medium text-[#EDEEF2]" data-id="element-169" data-mp="x5mtwc7l2789">What's simplified here</div>
        <ul className="mt-1.5 space-y-1 text-[12px] leading-[18px] text-[#9A9FB0]" data-id="element-170" data-mp="1b18iii9wmp05">
          {SIMPLIFICATIONS.map(s => <li key={s} className="flex gap-2" data-id="element-171" data-mp="1ksv1j0shhsdg">
              <span className="text-[#5D6272]" data-id="element-172" data-mp="1ksmnv6ojej17">–</span>
              {s}
            </li>)}
        </ul>
      </div>
    </Menu>;
}
function Menu({
  trigger,
  triggerClassName,
  label,
  children
}: {
  trigger: React.ReactNode;
  triggerClassName: string;
  label: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);
  return <div ref={rootRef} className="relative" data-id="element-173" data-mp="24gr78x98cwoe">
      <button type="button" aria-label={label} aria-expanded={open} onClick={() => setOpen(o => !o)} className={cx('transition-colors', triggerClassName)} data-id="element-174" data-mp="1pprz1ncb4a5c">
        {trigger}
      </button>
      {open && <div onClick={e => {
      if ((e.target as HTMLElement).closest('[data-close-menu]')) setOpen(false);
    }} className="absolute bottom-full right-0 mb-2 rounded-xl border border-white/10 bg-[#12141D] p-1 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.8)] animate-[fadeUp_0.18s_ease_both]" data-id="element-175" data-mp="1gnit7arzh4ya">
          {children}
        </div>}
    </div>;
}
function IconButton({
  label,
  onClick,
  active,
  className,
  children
}: {
  label: string;
  onClick: () => void;
  active?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return <button type="button" aria-label={label} aria-pressed={active} onClick={onClick} className={cx('flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08] hover:text-[#EDEEF2]', active === false ? 'text-[#5D6272]' : 'text-[#9A9FB0]', className)} data-id="element-176" data-mp="1i9ez7nlq64qw">
      {children}
    </button>;
}